import { addDays, format, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";
import * as XLSX from "xlsx";

export interface ExportableTimesheetRow {
  user_name: string;
  user_email: string;
  WeekStartDate: string;
  Monday: number;
  Tuesday: number;
  Wednesday: number;
  Thursday: number;
  Friday: number;
  Saturday: number;
  Sunday: number;
  total_hours: number;
  status: string;
}

export type ExportFormat = "excel" | "csv";

const DAY_OFFSETS = [
  { key: "Monday", day: "Monday", offset: 0 },
  { key: "Tuesday", day: "Tuesday", offset: 1 },
  { key: "Wednesday", day: "Wednesday", offset: 2 },
  { key: "Thursday", day: "Thursday", offset: 3 },
  { key: "Friday", day: "Friday", offset: 4 },
  { key: "Saturday", day: "Saturday", offset: 5 },
  { key: "Sunday", day: "Sunday", offset: 6 },
] as const;

/** Parse week-start as a local calendar date to avoid UTC day-shift. */
export const toLocalWeekDate = (weekStart: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(weekStart);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(weekStart);
};

export const isDateInRange = (date: Date, range?: DateRange): boolean => {
  if (!range?.from) return true;
  const day = startOfDay(date);
  const from = startOfDay(range.from);
  const to = startOfDay(range.to ?? range.from);
  return day >= from && day <= to;
};

type DailyExportRow = {
  Employee: string;
  Email: string;
  "Week Start": Date | string;
  Day: string;
  Date: Date | string;
  Hrs: number;
  Status: string;
};

type SummaryExportRow = {
  Employee: string;
  "Week Start": Date | string;
  Status: string;
  "Total Hours": number;
};

const expandToDailyRows = (
  rows: ExportableTimesheetRow[],
  formatType: ExportFormat,
  isDayIncluded: (date: Date) => boolean
): DailyExportRow[] => {
  const dailyRows: DailyExportRow[] = [];

  rows.forEach((entry) => {
    const weekStart = toLocalWeekDate(entry.WeekStartDate);

    DAY_OFFSETS.forEach(({ key, day, offset }) => {
      const date = addDays(weekStart, offset);
      if (!isDayIncluded(date)) return;

      dailyRows.push({
        Employee: entry.user_name,
        Email: entry.user_email,
        "Week Start":
          formatType === "excel" ? weekStart : format(weekStart, "MMM dd, yyyy"),
        Day: day,
        Date: formatType === "excel" ? date : format(date, "MMM dd, yyyy"),
        Hrs: Number(entry[key] ?? 0),
        Status: entry.status,
      });
    });
  });

  return dailyRows.sort((a, b) => {
    const employeeCompare = a.Employee.localeCompare(b.Employee, undefined, {
      sensitivity: "base",
      numeric: true,
    });
    if (employeeCompare !== 0) return employeeCompare;

    const weekA =
      a["Week Start"] instanceof Date
        ? a["Week Start"].getTime()
        : new Date(a["Week Start"]).getTime();
    const weekB =
      b["Week Start"] instanceof Date
        ? b["Week Start"].getTime()
        : new Date(b["Week Start"]).getTime();
    if (weekA !== weekB) return weekA - weekB;

    const dateA = a.Date instanceof Date ? a.Date.getTime() : new Date(a.Date).getTime();
    const dateB = b.Date instanceof Date ? b.Date.getTime() : new Date(b.Date).getTime();
    return dateA - dateB; // Monday → Sunday within the week
  });
};

const buildSummaryRows = (dailyRows: DailyExportRow[]): SummaryExportRow[] => {
  const summaryMap = new Map<string, SummaryExportRow>();

  dailyRows.forEach((row) => {
    const weekKey =
      row["Week Start"] instanceof Date
        ? row["Week Start"].getTime()
        : String(row["Week Start"]);
    const key = `${row.Employee}|${weekKey}|${row.Status}`;
    const existing = summaryMap.get(key);

    if (existing) {
      existing["Total Hours"] += row.Hrs;
    } else {
      summaryMap.set(key, {
        Employee: row.Employee,
        "Week Start": row["Week Start"],
        Status: row.Status,
        "Total Hours": row.Hrs,
      });
    }
  });

  return [...summaryMap.values()].sort((a, b) => {
    const employeeCompare = a.Employee.localeCompare(b.Employee, undefined, {
      sensitivity: "base",
      numeric: true,
    });
    if (employeeCompare !== 0) return employeeCompare;

    const weekA =
      a["Week Start"] instanceof Date
        ? a["Week Start"].getTime()
        : new Date(a["Week Start"]).getTime();
    const weekB =
      b["Week Start"] instanceof Date
        ? b["Week Start"].getTime()
        : new Date(b["Week Start"]).getTime();
    return weekA - weekB; // oldest week first
  });
};

const getCellDisplayLength = (cell: XLSX.CellObject | undefined): number => {
  if (!cell || cell.v == null) return 0;
  if (cell.t === "d" || cell.v instanceof Date) return 12; // mmm dd, yyyy
  if (typeof cell.v === "number") return String(cell.v).length;
  return String(cell.v).length;
};

/** Approximate Excel autofit using character widths. */
const autofitColumns = (worksheet: XLSX.WorkSheet, minWidth = 10, maxWidth = 40) => {
  if (!worksheet["!ref"]) return;

  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  const cols: XLSX.ColInfo[] = [];

  for (let col = range.s.c; col <= range.e.c; col += 1) {
    let maxLen = minWidth;

    for (let row = range.s.r; row <= range.e.r; row += 1) {
      const address = XLSX.utils.encode_cell({ r: row, c: col });
      const length = getCellDisplayLength(worksheet[address]);
      maxLen = Math.max(maxLen, Math.min(length + 2, maxWidth));
    }

    cols.push({ wch: maxLen });
  }

  worksheet["!cols"] = cols;
};

const applyDateFormats = (worksheet: XLSX.WorkSheet, dateCols: number[]) => {
  if (!worksheet["!ref"]) return;

  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
    dateCols.forEach((col) => {
      const address = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = worksheet[address];
      if (!cell) return;

      if (cell.v instanceof Date) {
        cell.t = "d";
        cell.z = "mmm dd, yyyy";
      } else if (typeof cell.v === "number") {
        cell.t = "n";
        cell.z = "mmm dd, yyyy";
      }
    });
  }
};

const buildFilename = (
  formatType: ExportFormat,
  counts: { employees: number; dateRange: boolean; statuses: number }
) => {
  const parts: string[] = [];
  if (counts.employees > 0) parts.push(`${counts.employees}emp`);
  if (counts.dateRange) parts.push("daterange");
  if (counts.statuses > 0) parts.push(`${counts.statuses}status`);
  const suffix = parts.length > 0 ? `_${parts.join("_")}` : "";
  const extension = formatType === "excel" ? "xlsx" : "csv";
  return `timesheet_report${suffix}.${extension}`;
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadFilteredTimesheets = (
  rows: ExportableTimesheetRow[],
  formatType: ExportFormat = "excel",
  filterCounts: { employees: number; dateRange: boolean; statuses: number } = {
    employees: 0,
    dateRange: false,
    statuses: 0,
  },
  isDayIncluded: (date: Date) => boolean = () => true
) => {
  const exportRows = expandToDailyRows(rows, formatType, isDayIncluded);

  if (exportRows.length === 0) {
    throw new Error("No daily rows to export for the selected filters.");
  }

  const timesheetSheet = XLSX.utils.json_to_sheet(exportRows, {
    cellDates: formatType === "excel",
  });

  const workbook = XLSX.utils.book_new();

  if (formatType === "excel") {
    applyDateFormats(timesheetSheet, [2, 4]); // Week Start, Date
    autofitColumns(timesheetSheet);

    const summaryRows = buildSummaryRows(exportRows);
    const summarySheet = XLSX.utils.json_to_sheet(summaryRows, { cellDates: true });
    applyDateFormats(summarySheet, [1]); // Week Start
    autofitColumns(summarySheet);

    // Summary first, then Timesheets
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
    XLSX.utils.book_append_sheet(workbook, timesheetSheet, "Timesheets");
  } else {
    XLSX.utils.book_append_sheet(workbook, timesheetSheet, "Timesheets");
  }

  const filename = buildFilename(formatType, filterCounts);
  const bookType = formatType === "excel" ? "xlsx" : "csv";
  const arrayBuffer = XLSX.write(workbook, {
    bookType,
    type: "array",
    cellDates: formatType === "excel",
  });
  const mimeType =
    formatType === "excel"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "text/csv;charset=utf-8;";

  triggerDownload(new Blob([arrayBuffer], { type: mimeType }), filename);
};
